import React, { useEffect, useState } from "react";
import { useDispatch } from "react-redux";
import { useParams, useNavigate, useLocation } from "react-router-dom";
import { toast } from "react-toastify";
import Button from "../../components/Button";
import FrontendDataTable from "../../components/FrontendDataTable";
import Loader from "../../components/Loader";
import PaddingContainer from "../../components/PaddingContainer";
import Tab from "../../components/Tab";
import TabsContainer from "../../components/TabsContainer";
import TableValue from "../../components/TableValue";
import Title from "../../components/Title";
import UserInformation from "../../components/UserInformation";
import { DownloadPDFIcon } from "../../constant";
import moment from "moment/moment";
import { formatCurrency, formatNumberWithCommas, formattedDate } from "../../helpers";
import { exportUserDetailToPDF } from "../../pdfs/users/userDetail";
import { setPageLink, setPageTitle } from "../../store/pageSlice";
import useApi from "../../useApi";
import classes from "./UserDetail.module.css";
import { bookingStatusOptions } from "../../data";
import PaymentStatusIndicator from "../../components/PaymentStatusIndicator";
import StatusIndicator from "../../components/StatusIndicator";
import { paymentMethods } from "../../data";    
const UserDetail = () => {
  const dispatch = useDispatch();
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(false);
  const [activeTab, setActiveTab] = useState(0);
  const [bookings, setBookings] = useState([]);
  const [payments, setPayments] = useState([]);
  const { id } = useParams();
  const { getUsers } = useApi();
  const navigate = useNavigate();
  const location = useLocation();

  const getUserById = async () => {
    try {
      setLoading(true);
      const res = await getUsers({
        Id: parseInt(id, 10),
        pageNumber: 1,
        pageSize: 1,
        ShowInactive: true,
      });
      
      if (res.data?.headers?.error === 200 && res.data?.body?.items?.length > 0) {
        setUser(res.data.body.items[0]);

        // Set bookings data for the table
        if (res.data.body.items[0]?.bookings?.length) {
          setBookings(res.data.body.items[0].bookings);
        }
        if (res.data.body.items[0]?.payments?.length) {
          setPayments(res.data.body.items[0].payments);
        }
      } else {
        toast.error("User not found");
        navigate("/users");
      }
    } catch (error) {
      console.error("Error fetching user:", error);
      toast.error("Failed to load user data");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
  console.log("location.state", location.state)
    // Check if we have user data from navigation state
    if (location.state?.userData) {
      setUser(location.state.userData);
      
      // Set bookings and payments data if available
      if (location.state.userData?.bookings?.length) {
        setBookings(location.state.userData.bookings);
      }
      if (location.state.userData?.payments?.length) {
        setPayments(location.state.userData.payments);
      }
    } else {
      // No state data, need to fetch from API
      getUserById();
    }
    
    dispatch(setPageTitle("User Detail"));
    dispatch(setPageLink("/users"));
  }, [id, location.state]);

  // Define columns for the booking history table
  const bookingColumns = [
    {
      field: "bookingNo",
      header: "Reference ID",
      sortable: true,
      body: (rowData) => {
        return <TableValue top={rowData?.bookingNo?.split("-")?.slice(1).join("-")} />;
      },
    },
    {
      field: "courtName",
      header: "Court Name",
      sortable: true,
      body: (rowData) => <TableValue top={rowData?.courtName} />
    },
    {
      field: "bookingGrossAmount",
      header: "Amount",
      sortable: true,
          body: (rowData) => <TableValue top={formatNumberWithCommas(rowData.bookingGrossAmount)} />
    },
    {
      field: "bookingDate",
      header: "Date & Time",
      sortable: true,
      body: (rowData) => (
        <TableValue
          top={formattedDate(rowData.bookingDate)}
          down={moment(rowData.bookingDate).format("hh:mm A")}  
        />
      ),
    },
    {
      field: "bookingPaymentStatusId",
      header: "Payment Status",
      sortable: true,
      body: (rowData) => {
        const paymentStatus = rowData.bookingPaymentStatusId;

        return <PaymentStatusIndicator bookingStatus={paymentStatus} />;
      },
    },
    {
      field: "bookingStatusId",
      header: "Booking Status",
      sortable: true,
      body: (rowData) => {
        const bookingStatus = rowData.bookingStatusId;
        return <StatusIndicator bookingStatus={bookingStatus} />;
      },
    },
  ];

  const paymentColumns = [
    {
      field: "paymentNo",
      header: "Payment ID",
      sortable: true,
      body: (rowData) => {
        return <TableValue top={rowData?.payNo?.split("-")?.slice(1).join("-")} />;
      },
    },
    {
      field: "bookingNo",
      header: "Reference ID",
      sortable: true,
      body: (rowData) => {
        return <TableValue top={rowData?.bookingNo?.split("-")?.slice(1).join("-")} />;
      },
    },
    {
      field: "locationName",
      header: "Location",
      sortable: true,
     body: (rowData) => <TableValue top={rowData?.locationName} />
    },
    {
      field: "paymentDate",
      header: "Date & Time",
      sortable: true,
      body: (rowData) => (
        <TableValue
          top={`  ${formattedDate(rowData?.paymentDate)}`}
          down={` ${moment(rowData?.paymentDate, "HH:mm:ss").format(
         "hh:mm A"
       )} `}
        />
      ),
    },
   
    {
      field: "paymentModeId",
      header: "Payment Mode",
      sortable: true,
      body: (rowData) => {
        const paymentMode = paymentMethods?.find(item => item?.value === rowData?.paymentModeId)?.label;
        return <TableValue top={paymentMode} />;
      },
    },
    {
      field: "amount",
      header: "Amount",
      sortable: true,
      body: (rowData) => <TableValue top={formatNumberWithCommas(rowData.amount)} />  
    },
    {
      field: "paymentStatusId",
      header: "Status",
      sortable: true,
      body: (rowData) => {
        
        const status = rowData?.paymentStatusId;
      
        return <PaymentStatusIndicator bookingStatus={status} />;
      },
    },
  ];

  return (
    <>
      {loading ? (
        <Loader />
      ) : (
        <>
          <PaddingContainer>
            <header className={classes.header}>
              <Title title="User Detail" />
              <div className={classes.headerActions}>
                <Button
                  className={classes.downloadButton}
                  label={"Download PDF"}
                  leftIcon={
                    <span className={classes.downloadIcon}>
                      <DownloadPDFIcon className={classes.icon} />
                    </span>
                  }
                  onClick={() => exportUserDetailToPDF(user)}
                />
              </div>
            </header>
            <UserInformation user={user} />
            <section className={classes.tabsContainer}>
              <TabsContainer
                tabs={
                  <>
                    <Tab
                      label="Booking History"
                      active={activeTab === 0}
                      onClick={() => setActiveTab(0)}
                    />
                    <Tab
                      label="Payment History"
                      active={activeTab === 1}
                      onClick={() => setActiveTab(1)}
                    />
                  </>
                }
              ></TabsContainer>

              {activeTab === 0 && (
                <FrontendDataTable
                  data={bookings}
                  columns={bookingColumns}
                  defaultRows={10}
                  rowsPerPageOptions={[5, 10, 25, 50]}
                  emptyMessage="No bookings found"
                  className={classes.bookingTable}
                />
              )}
              {activeTab === 1 && (
                <FrontendDataTable
                  data={payments}
                  columns={paymentColumns}
                  defaultRows={10}
                  rowsPerPageOptions={[5, 10, 25, 50]}
                  emptyMessage="No bookings found"
                  className={classes.bookingTable}
                />
                // <div className={classes.paymentHistoryContainer}>
                //   <p>Payment history information will be displayed here.</p>
                // </div>
              )}
            </section>
          </PaddingContainer>
        </>
      )}
    </>
  );
};

export default UserDetail;
